import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";
import type { ScopeState } from "../components/scope-selector";
import type { DCConditionData } from "../components/condition";
import type { LogicalOperator } from "../../if/types";
import type { ActionItem } from "../components/action-selector";
import type { DataCenterSource, ValueColumn, ValueInputType } from "../types";
import type { Table } from "@/queries/data-center/data-center.api";
import type { Variable } from "../../types";
import type { ApiNode } from "@/workflow_api/types/workflow";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useGetNode } from "@/workflow_api/services/nodes.api";
import {
  useGetTableDetail,
  useGetTablesWithSource,
} from "@/queries/data-center/data-center.api";
import { actionList } from "../components/action-selector";
import {
  getDeserializedNode,
  getNodeAction,
  getSelectedTable,
  getTableSource,
  parseType,
} from "../serialization/deserialization";
import { getSerializedNode } from "../serialization";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { isValidNumericRange } from "@/utils/format";
import { getSourcesUpTo, getNextSource, isLastSource } from "./sources-helper";

// Helper function to get placeholder text based on column type
const getPlaceholderForType = (type: ValueInputType): string => {
  switch (type) {
    case "string":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    case "regex":
      return "Escribe una expresión regular";
    case "positive-integer":
      return "Escribe un número entero positivo";
    default:
      return "Ingresa un valor";
  }
};

const currentColumns: ValueColumn[] = [
  {
    label: "Nombre de la columna",
    isRequired: true,
    name: "column_text",
    valueType: "string",
    placeholder: "Escribe un texto",
    value: "",
    inputType: "input",
  },
  {
    label: "Nombre de la columna",
    isRequired: false,
    name: "column_number",
    valueType: "number",
    placeholder: "Escribe un número",
    value: "",
    inputType: "input",
  },
  {
    label: "Nombre de la columna",
    isRequired: true,
    name: "column_boolean",
    valueType: "boolean",
    placeholder: "Elige un booleano",
    value: "true",
    inputType: "input",
  },
  {
    label: "Nombre de la columna",
    isRequired: true,
    name: "column_variable",
    valueType: "variable",
    placeholder: "Elige una variable",
    value: "",
    inputType: "input",
  },
  {
    label: "Nombre de la columna",
    isRequired: false,
    name: "column_texto_variable",
    valueType: "string",
    placeholder: "Escribe un texto",
    value: "",
    inputType: "input",
  },
];

const initScopeState: ScopeState = {
  comparativeValue: "",
  operationType: "input",
  scope: {
    id: "created_at",
    name: "Fecha de creación",
    type: "dateScope",
    data_type: "date",
  },
  conditionScope: { name: "ANY", label: "Cualquiera" },
  initialRange: { value: "", type: "input" },
  endRange: { value: "", type: "input" },
  dateRangeError: "",
};

// Zod: schema & derived validation state for variable name
const variableNameSchema = z
  .string()
  .trim()
  .min(1, "El nombre de la variable es requerido")
  .max(255, "Máximo 255 caracteres")
  .regex(
    /^[A-Za-z][A-Za-z0-9_-]*$/,
    "El nombre de la variable debe iniciar con una letra y solo puede contener letras, números y guiones.",
  );

const DataCenterNodeContext = createContext<{
  scopeState: ScopeState;
  setScopeState: (state: ScopeState) => void;
  isValidScope: boolean;
  multipleSelected: boolean;
  setMultipleSelected: (value: boolean) => void;
  multipleCount: number;
  setMultipleCount: (value: number) => void;
  variableName: string;
  setVariableName: (name: string) => void;
  isVariableNameValid: boolean;
  variableNameError: string | null;
  reset: () => void;
  conditions: DCConditionData[];
  setConditions: (conditions: DCConditionData[]) => void;
  logicalOperator: LogicalOperator;
  setLogicalOperator: (operator: LogicalOperator) => void;
  conditionsAreValid: boolean;
  selectedTable: Table | undefined;
  setSelectedTable: (table: Table) => void;
  tables: Table[];
  setTables: (tables: Table[]) => void;
  selectedAction: ActionItem | undefined;
  setSelectedAction: (action: ActionItem) => void;
  columns: ValueColumn[];
  setColumns: (columns: ValueColumn[]) => void;
  columnsAreValid: boolean;
  columnsDef: Variable[];
  setColumnsDef: (columns: Variable[]) => void;
  currentNode: ApiNode | null;
  setCurrentNode: (node: ApiNode | null) => void;
  isReadyToShow: boolean;
  isLoadingTableDetail: boolean;
  saveNode: (title: string, manual_continue?: boolean) => void;
  nodeName: string | undefined;
  // pagination for tables
  loadMoreTables: () => void;
  hasMoreTables: boolean;
  isLoadingMoreTables: boolean;
  // search for tables
  tableSearchQuery: string;
  setTableSearchQuery: (query: string) => void;
  executeQuery: boolean;
  setExecuteQuery: (value: boolean) => void;
}>({
  scopeState: initScopeState,
  setScopeState: () => {},
  isValidScope: true,
  multipleSelected: false,
  setMultipleSelected: () => {},
  multipleCount: 50,
  setMultipleCount: () => {},
  variableName: "",
  setVariableName: () => {},
  isVariableNameValid: true,
  variableNameError: null,
  reset: () => {},
  conditions: [],
  setConditions: () => {},
  logicalOperator: "and",
  setLogicalOperator: () => {},
  conditionsAreValid: true,
  selectedAction: undefined,
  selectedTable: undefined,
  setSelectedTable: () => {},
  tables: [],
  setTables: () => {},
  setSelectedAction: () => {},
  columns: [],
  setColumns: () => {},
  columnsAreValid: true,
  columnsDef: [],
  setColumnsDef: () => {},
  currentNode: null,
  setCurrentNode: () => {},
  isReadyToShow: false,
  isLoadingTableDetail: false,
  saveNode: () => {},
  nodeName: undefined,
  loadMoreTables: () => {},
  hasMoreTables: false,
  isLoadingMoreTables: false,
  tableSearchQuery: "",
  setTableSearchQuery: () => {},
  executeQuery: false,
  setExecuteQuery: () => {},
});

export const DataCenterNodeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { flowId } = useWorkflowStore();
  const { variables: inputVariables, isLoading: loadingVariables } =
    useGetVariables();
  const { selectedNodeId, nodeDetailsOpen } = useUIStore();
  const [selectedTable, setSelectedTable] = useState<Table>();
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedAction, setSelectedAction] = useState<ActionItem>();
  const [scopeState, setScopeState] = useState<ScopeState>(initScopeState);
  const [multipleSelected, setMultipleSelected] = useState<boolean>(false);
  const [executeQuery, setExecuteQuery] = useState<boolean>(false);
  const [multipleCount, setMultipleCount] = useState<number>(50);
  const [variableName, setVariableName] = useState<string>("");
  const [conditions, setConditions] = useState<DCConditionData[]>([
    {
      id: new Date().getTime().toString(),
      variable: null,
      operation: null,
      comparativeValue: "",
      initialRangeValue: "",
      endRangeValue: "",
      logicalOperator: "and",
      operationValueType: "input",
      initialRangeValueType: "input",
      endRangeValueType: "input",
    },
  ]);
  const [columns, setColumns] = useState<ValueColumn[]>(currentColumns);
  const [columnsDef, setColumnsDef] = useState<Variable[]>();
  const [currentNode, setCurrentNode] = useState<ApiNode | null>(null);
  const [logicalOperator, setLogicalOperator] =
    useState<LogicalOperator>("and");
  const { mutateAsync: getNode } = useGetNode();
  const [page, setPage] = useState<number>(1);
  const [sourceTables, setSourceTables] = useState<DataCenterSource>("sdt");
  const [hasMoreTables, setHasMoreTables] = useState<boolean>(false);
  const [targetSource, setTargetSource] = useState<DataCenterSource | null>(
    null,
  );
  const [sourcesToFetch, setSourcesToFetch] = useState<DataCenterSource[]>([]);
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [isReadyToShow, setIsReadyToShow] = useState(false);
  const {
    data: dataTables,
    isLoading: isLoadingTables,
    isFetching: isFetchingTables,
    error: tablesError,
  } = useGetTablesWithSource(sourceTables, {
    page,
    per_page: 100,
    orderDirection: "desc",
    columns: 1,
    ...(tableSearchQuery ? { search: tableSearchQuery } : {}),
  });
  const { data: tableDetail, isFetching: isLoadingTableDetail } =
    useGetTableDetail(selectedTable);

  const hasDeserializedRef = useRef<string | null>(null);
  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  const reset = () => {
    setScopeState(initScopeState);
    setMultipleSelected(false);
    setMultipleCount(50);
    setVariableName("");
    setConditions([
      {
        id: new Date().getTime().toString(),
        variable: null,
        operation: null,
        comparativeValue: "",
        initialRangeValue: "",
        endRangeValue: "",
        logicalOperator: "and",
        operationValueType: "input",
        initialRangeValueType: "input",
        endRangeValueType: "input",
      },
    ]);
    setLogicalOperator("and");
  };

  /**
   * set columns on table details and update value columns for create/update forms
   */
  useEffect(() => {
    const next = tableDetail?.data?.columns?.map(
      (column: { id: string; name: string; type: string }) => ({
        id: column.id,
        name: column.name,
        type: parseType(column.type),
      }),
    );
    setColumnsDef(next);
  }, [tableDetail]);

  /**
   * Generate value columns for create/update forms when table or action changes
   * This ensures columns are regenerated when switching tables
   */
  useEffect(() => {
    if (!columnsDef || !selectedAction) return;

    // Only generate columns for create and update operations
    if (selectedAction.id === "create" || selectedAction.id === "update") {
      const valueColumns: ValueColumn[] = columnsDef.map(
        (column: Variable) => ({
          label: column.name,
          isRequired: false, // You might want to check if the column has constraints
          name: column.id,
          valueType: column.type as ValueInputType,
          placeholder: getPlaceholderForType(column.type as ValueInputType),
          value: "",
          inputType: "input",
        }),
      );
      setColumns(valueColumns);
      // Reset deserialization when columns are regenerated due to table change
      if (hasDeserializedRef.current) {
        hasDeserializedRef.current = null;
      }
    }
  }, [columnsDef, selectedAction]);

  /**
   * Reset columns and selected action when no table is selected
   */
  useEffect(() => {
    if (!selectedTable) {
      setColumns([]);
      setColumnsDef([]);
    }
  }, [selectedTable]);

  /**
   * Reset selected action when table changes (to a different table)
   * This ensures the action is reset when switching between tables
   * Skip for deserialization
   */
  useEffect(() => {
    if (
      selectedTable &&
      hasDeserializedRef.current &&
      !hasDeserializedRef.current.includes("_partial")
    ) {
      setSelectedAction(undefined);
    }
  }, [selectedTable]);

  /**
   * Reset tables when search query changes
   */
  useEffect(() => {
    if (!tableSearchQuery || tableSearchQuery.trim() === "") {
      setTables([]);
      setPage(1);
      setSourceTables("sdt");
    }
  }, [tableSearchQuery]);

  /**
   * set tables on data load
   */
  useEffect(() => {
    if (!dataTables?.data) return;
    // Update hasMore based on meta
    const meta = dataTables.meta;
    const nextHasMore = meta ? meta.current_page < meta.last_page : false;
    setHasMoreTables(nextHasMore);
    if (tableSearchQuery && tableSearchQuery.trim() !== "") {
      setTables(dataTables.data);
    } else {
      setTables((prev) => {
        // If switching to a new source, always append new data to previous tables
        // Use combination of id and source to identify unique tables
        const existingKeys = new Set(prev.map((t) => `${t.id}-${t.source}`));
        const appended = dataTables.data.filter(
          (t) => !existingKeys.has(`${t.id}-${t.source}`),
        );
        return [...prev, ...appended];
      });
    }
  }, [dataTables, page, tableSearchQuery]);

  // Handle error and switch to next source if current fails
  // Uses helper function to get next source in order
  useEffect(() => {
    if (!tablesError) return;
    const nextSource = getNextSource(sourceTables);
    if (nextSource) {
      setSourceTables(nextSource);
      setHasMoreTables(true);
      setPage(1);
    }
  }, [tablesError, sourceTables]);

  // handle pagination and source change for pagination
  const loadMoreTables = () => {
    if (isLoadingTables) return;
    if (isFetchingTables) return;
    if (hasMoreTables) {
      setPage((p) => p + 1);
      return;
    }
    if (!isLastSourceElement) {
      const nextSource = getNextSource(sourceTables);
      if (nextSource) {
        setSourceTables(nextSource);
        setPage(1);
      }
    }
  };

  /**
   * reset deserialization reference when node details are closed
   */
  useEffect(() => {
    if (nodeDetailsOpen === false) {
      hasDeserializedRef.current = null;
      setIsReadyToShow(false);
      setTargetSource(null);
      setSourcesToFetch([]);
    }
  }, [nodeDetailsOpen]);

  /**
   * fetch node details when node is selected
   */
  useEffect(() => {
    if (!selectedNodeId || !nodeDetailsOpen) return;
    const run = async () => {
      try {
        const node = await getNode(selectedNodeId);
        if (node) setCurrentNode(node);
      } catch (error) {
        console.error("[DataCenter] getNode error:", error);
      }
    };
    run();
  }, [selectedNodeId, nodeDetailsOpen, getNode]);

  /**
   * Deserialize the selected table and action when details is opened and
   * ref is selected
   */
  useEffect(() => {
    if (!nodeDetailsOpen || !currentNode) return;
    if (hasDeserializedRef.current === currentNode.id) return;
    if (!dataTables?.data) return;
    if (loadingVariables) return;
    if (!currentNode.inputs || currentNode.inputs.length === 0) {
      // Mark as loaded if no inputs are present
      setIsReadyToShow(true);
      hasDeserializedRef.current = currentNode.id ?? "__unknown__";
      return;
    }
    const nodeSource = getTableSource(currentNode);
    if (targetSource === null) {
      // delegate the source fetching to the progression effect
      // table and action selection will be handled after sources are loaded
      const sources = getSourcesUpTo(nodeSource);
      setTargetSource(nodeSource);
      setSourcesToFetch(sources);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeDetailsOpen, currentNode, loadingVariables, dataTables]);

  /**
   * Handle source progression during deserialization
   */
  useEffect(() => {
    if (!currentNode) return;
    if (hasDeserializedRef.current === currentNode.id) return;
    if (!targetSource || sourcesToFetch.length === 0) return;
    if (selectedTable) return;
    const currentSourceIndex = sourcesToFetch.indexOf(sourceTables);
    if (isFetchingTables) {
      return;
    }
    if (sourceTables === targetSource && !isFetchingTables) {
      // Reached target source and finished loading
      const table = getSelectedTable(currentNode, tables);
      if (table) {
        setSelectedTable(table);
        const action = getNodeAction(currentNode, actionList);
        if (action) {
          setSelectedAction(action);
        }
        // Mark as partially deserialized to prevent resets
        hasDeserializedRef.current = `${currentNode.id}_partial`;
      } else if (hasMoreTables) {
        loadMoreTables();
      } else {
        setIsReadyToShow(true);
      }
      // Filters and columns will be handled by the next effect when columnsDef is ready
      return;
    }

    if (currentSourceIndex < sourcesToFetch.length - 1) {
      loadMoreTables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    targetSource,
    sourcesToFetch,
    sourceTables,
    isFetchingTables,
    hasMoreTables,
    tables,
    currentNode,
  ]);

  /**
   * Deserialize the filters when columns are ready
   * Close the ref
   *
   */
  useEffect(() => {
    if (!nodeDetailsOpen || !currentNode) return;
    if (!columnsDef) return;
    // Check if already fully deserialized (not just partial)
    if (hasDeserializedRef.current === currentNode.id) return;
    if (!columnsDef) return;
    if (!selectedAction) return;
    if (loadingVariables) return; // Do not deserialize until loading variables is false

    if (
      selectedAction.id === "read" ||
      selectedAction.id === "delete" ||
      selectedAction.id === "update"
    ) {
      const {
        filters,
        isMultiple,
        logicalOperator,
        variableName,
        scope,
        columns,
        limit,
        executeQuery,
      } = getDeserializedNode(currentNode, columnsDef, inputVariables);
      setMultipleSelected(isMultiple);
      setExecuteQuery(executeQuery);
      // if no filters, initialize with one empty condition
      setConditions(
        filters.length > 0
          ? filters
          : [
              {
                id: new Date().getTime().toString(),
                variable: null,
                operation: null,
                comparativeValue: "",
                initialRangeValue: "",
                endRangeValue: "",
                logicalOperator: "and",
                operationValueType: "input",
                initialRangeValueType: "input",
                endRangeValueType: "input",
              },
            ],
      );
      setLogicalOperator(logicalOperator);
      setVariableName(variableName);
      setMultipleCount(limit ?? 50);
      if (scope) setScopeState(scope);
      // Only set columns for update operation if we have deserialized data
      if (selectedAction.id === "update" && columns && columns.length > 0) {
        // Merge deserialized values with current column structure
        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            const deserializedCol = columns.find((dc) => dc.name === col.name);
            return deserializedCol ? { ...col, ...deserializedCol } : col;
          }),
        );
      }
    } else if (selectedAction.id === "create") {
      const { variableName, columns } = getDeserializedNode(
        currentNode,
        columnsDef,
        inputVariables,
      );
      setVariableName(variableName);
      // Only set columns if we have deserialized data, merge with current structure
      if (columns && columns.length > 0) {
        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            const deserializedCol = columns.find((dc) => dc.name === col.name);
            return deserializedCol ? { ...col, ...deserializedCol } : col;
          }),
        );
      }
    }
    hasDeserializedRef.current = currentNode.id ?? "__unknown__";
    setIsReadyToShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeDetailsOpen, currentNode, columnsDef, loadingVariables]);

  /**
   * Serialize and save the node
   * @param title
   * @param manual_continue
   */
  const saveNode = (title: string, manual_continue?: boolean) => {
    if (selectedTable && selectedAction && currentNode) {
      updateNode(
        getSerializedNode(
          selectedAction.id,
          currentNode,
          title,
          flowId,
          selectedTable.name,
          selectedTable.source ?? "sdt",
          multipleSelected,
          conditions,
          logicalOperator,
          scopeState,
          variableName,
          multipleCount,
          columns,
          manual_continue,
          executeQuery,
        ),
      );
    }
  };

  const variableNameParse = variableNameSchema.safeParse(variableName);
  const isVariableNameValid = variableNameParse.success;
  const variableNameError = isVariableNameValid
    ? null
    : variableNameParse.error.issues[0]?.message || "Nombre inválido";
  const isLastSourceElement = useMemo(
    () => isLastSource(sourceTables),
    [sourceTables],
  );

  return (
    <DataCenterNodeContext.Provider
      value={{
        selectedAction,
        setSelectedAction,
        selectedTable,
        setSelectedTable,
        tables,
        setTables,
        scopeState,
        setScopeState,
        reset,
        setVariableName,
        variableName,
        isVariableNameValid,
        variableNameError,
        multipleSelected,
        setMultipleSelected,
        multipleCount,
        setMultipleCount,
        conditions,
        setConditions,
        logicalOperator,
        setLogicalOperator,
        columns,
        setColumns,
        columnsDef: columnsDef || [],
        setColumnsDef,
        currentNode,
        setCurrentNode,
        isLoadingTableDetail,
        saveNode,
        nodeName: currentNode?.display_name,
        loadMoreTables,
        hasMoreTables: hasMoreTables || !isLastSourceElement,
        isLoadingMoreTables: isFetchingTables,
        isReadyToShow,
        executeQuery,
        setExecuteQuery,
        isValidScope:
          scopeState.conditionScope.name !== "ANY"
            ? (scopeState.conditionScope.needsComparativeValue &&
                !!scopeState.comparativeValue) ||
              !!(
                scopeState.conditionScope.needsComparativeRange &&
                !!scopeState.initialRange?.value &&
                !!scopeState.endRange?.value &&
                scopeState.dateRangeError === ""
              )
            : true,
        conditionsAreValid: conditions.every(
          (condition) =>
            condition.variable &&
            condition.operation &&
            (!condition.operation.needsComparativeValue ||
              (condition.operation.needsComparativeValue &&
                condition.comparativeValue !== "")) &&
            (!condition.operation?.needsComparativeRange ||
              (condition.operation?.needsComparativeRange &&
                condition.initialRangeValue !== "" &&
                condition.endRangeValue !== "" &&
                isValidNumericRange(condition))),
        ),
        columnsAreValid: columns.every((col) =>
          col.isRequired ? col.value !== "" : true,
        ),
        tableSearchQuery,
        setTableSearchQuery,
      }}
    >
      {children}
    </DataCenterNodeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDataCenterNode = () => {
  if (!DataCenterNodeContext) {
    throw new Error(
      "useDataCenterNode must be used within a DataCenterNodeProvider.",
    );
  }
  return useContext(DataCenterNodeContext);
};
